import { useEffect, useState, useCallback } from "react";

const detectIOS = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const detectStandalone = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }
  return window.navigator.standalone === true;
};

/**
 * Hook that captures the `beforeinstallprompt` event (Android/Chromium PWA install)
 * and exposes a `promptInstall` trigger. Falls back to a flag for iOS Safari
 * (which uses Share → Add to Home Screen manually).
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [isIOS] = useState(detectIOS);
  const [isStandalone] = useState(detectStandalone);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: "unavailable" };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice && choice.outcome === "accepted") setInstalled(true);
    return choice;
  }, [deferredPrompt]);

  return {
    canInstall: Boolean(deferredPrompt),
    installed,
    isIOS,
    isStandalone,
    promptInstall,
  };
}

export default usePWAInstall;
